import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCampaigns } from "@/hooks/useCampaigns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  Megaphone,
  Sparkles,
  X,
  ShieldCheck,
} from "lucide-react";
import {
  type InfluencerPreview,
  type AddToCampaignModalProps,
  type AssignedResult,
  InfluencerSelectionList,
  CampaignSelectionList,
} from "./influencers/add-to-campaign";

export type { InfluencerPreview, AddToCampaignModalProps, AssignedResult };

export const AddToCampaignModal: React.FC<AddToCampaignModalProps> = ({
  open,
  onOpenChange,
  influencers,
  onAssigned,
}) => {
  const { data: campaigns = [], isLoading: campaignsLoading } = useCampaigns();
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");
  const [selectedInfluencerIds, setSelectedInfluencerIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [alreadyAssignedIds, setAlreadyAssignedIds] = useState<string[]>([]);

  // Initialize selected influencers when modal opens or influencers list changes
  useEffect(() => {
    if (open) {
      setSelectedInfluencerIds(influencers.map((i) => i.id));
      setSelectedCampaignId("");
      setSearchQuery("");
      setAlreadyAssignedIds([]);
    }
  }, [open, influencers]);

  // Check which of the influencers are already assigned to the selected campaign
  useEffect(() => {
    async function fetchAlreadyAssigned() {
      if (!selectedCampaignId || influencers.length === 0) {
        setAlreadyAssignedIds([]);
        return;
      }
      const { data, error } = await supabase
        .from("campaign_influencers")
        .select("influencer_id")
        .eq("campaign_id", selectedCampaignId)
        .in(
          "influencer_id",
          influencers.map((i) => i.id)
        );

      if (error) {
        setAlreadyAssignedIds([]);
      } else {
        setAlreadyAssignedIds((data ?? []).map((d) => d.influencer_id));
      }
    }
    fetchAlreadyAssigned();
  }, [selectedCampaignId, influencers]);

  // Filter campaigns to exclude completed campaigns and match search query
  const filteredCampaigns = useMemo(() => {
    const activeAndDraftCampaigns = campaigns.filter(
      (c) => (c.status || "").trim().toLowerCase() !== "completed"
    );
    if (!searchQuery.trim()) return activeAndDraftCampaigns;
    const q = searchQuery.toLowerCase();
    return activeAndDraftCampaigns.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.brand?.toLowerCase().includes(q) ||
        c.brand_name?.toLowerCase().includes(q)
    );
  }, [campaigns, searchQuery]);

  const handleToggleInfluencer = (id: string) => {
    setSelectedInfluencerIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleClearAll = () => {
    setSelectedInfluencerIds([]);
  };

  const handleSelectAll = () => {
    setSelectedInfluencerIds(influencers.map((i) => i.id));
  };

  const activeSelectedCount = selectedInfluencerIds.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCampaignId || activeSelectedCount === 0) return;

    setSubmitting(true);

    const toAssignIds = selectedInfluencerIds.filter(
      (id) => !alreadyAssignedIds.includes(id)
    );

    if (toAssignIds.length === 0) {
      toast({
        title: "Already Added",
        description: "All selected influencers are already in this campaign.",
        variant: "default",
      });
      setSubmitting(false);
      return;
    }

    const records = toAssignIds.map((id) => ({
      campaign_id: selectedCampaignId,
      influencer_id: id,
      status: "contacted" as const,
    }));

    const { error } = await supabase
      .from("campaign_influencers")
      .insert(records, { count: "exact" });

    setSubmitting(false);

    if (error) {
      toast({
        title: "Error adding influencers",
        description: error.message,
        variant: "destructive",
      });
    } else {
      const campaign = campaigns.find((c) => c.id === selectedCampaignId);
      const campaignName = campaign?.name ?? "Campaign";
      toast({
        title: "Success",
        description: `✅ ${toAssignIds.length} influencer${
          toAssignIds.length === 1 ? "" : "s"
        } added to "${campaignName}".`,
      });

      if (typeof onAssigned === "function") {
        onAssigned({
          count: toAssignIds.length,
          campaignName,
        });
      }
      onOpenChange(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[920px] w-full p-0 overflow-hidden border-0 sm:rounded-3xl shadow-2xl bg-white focus:outline-none [&>button]:hidden">
          {/* Header */}
          <div className="px-7 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-[#FAF5FF] border border-[#F3E8FF] flex items-center justify-center text-[#A655F8] shrink-0 shadow-xs">
                <Megaphone className="w-6 h-6" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">
                  Add to Campaign
                </DialogTitle>
                <DialogDescription className="text-xs sm:text-sm text-slate-500 mt-0.5">
                  Assign {activeSelectedCount} influencer
                  {activeSelectedCount === 1 ? "" : "s"} to an active campaign
                  and start creating impact.
                </DialogDescription>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block select-none">
                <span className="font-tilt text-base font-medium text-[#A655F8] block tracking-wide italic transform -rotate-1">
                  Right creators.
                </span>
                <span className="font-tilt text-base font-medium text-[#A655F8] flex items-center justify-end gap-1 tracking-wide italic transform -rotate-1">
                  Bigger results.{" "}
                  <Sparkles className="w-3.5 h-3.5 fill-[#A655F8] text-[#A655F8]" />
                </span>
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="text-slate-400 hover:text-slate-600 p-2 rounded-xl hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Main 2-Column Content */}
          <div className="px-7 py-5 grid grid-cols-1 md:grid-cols-2 gap-6 bg-white max-h-[70vh] overflow-y-auto no-scrollbar">
            <InfluencerSelectionList
              influencers={influencers}
              selectedInfluencerIds={selectedInfluencerIds}
              alreadyAssignedIds={alreadyAssignedIds}
              onToggleInfluencer={handleToggleInfluencer}
              onClearAll={handleClearAll}
              onSelectAll={handleSelectAll}
            />

            <CampaignSelectionList
              campaigns={filteredCampaigns}
              campaignsLoading={campaignsLoading}
              selectedCampaignId={selectedCampaignId}
              onSelectCampaign={setSelectedCampaignId}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
            />
          </div>

          {/* Footer */}
          <div className="px-7 py-4 bg-white border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-slate-400 text-left w-full sm:w-auto">
              <ShieldCheck className="w-4 h-4 text-[#A655F8] shrink-0" />
              <span className="text-xs font-normal">
                Your data is secure and will only be used for this campaign.
              </span>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-xl px-5 h-10 border-slate-200 text-slate-700 hover:bg-slate-50 font-medium text-sm"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={handleSubmit}
                disabled={
                  submitting ||
                  !selectedCampaignId ||
                  activeSelectedCount === 0
                }
                className="rounded-xl px-5 h-10 bg-[#A655F8] hover:bg-[#9333EA] text-white font-semibold text-sm flex items-center gap-2 shadow-xs transition-colors"
              >
                <Megaphone className="w-4 h-4" />
                {submitting
                  ? "Adding..."
                  : `Add ${activeSelectedCount} Influencer${
                      activeSelectedCount === 1 ? "" : "s"
                    } to Campaign`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddToCampaignModal;
